import { useState, useEffect, useRef } from 'react';

export default function GenesisSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="py-20 bg-gradient-to-b from-black to-gray-900 relative overflow-hidden"
    >
      {/* Background Elements */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-10 w-32 h-32 bg-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-red-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <div className={`transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            <h2 className="text-4xl md:text-5xl font-bold mb-8">
              <span className="bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent">
                Our Genesis:
              </span>
              <br />
              <span className="text-white">The Early Days of Digital</span>
            </h2>

            <h3 className="text-2xl md:text-3xl font-semibold text-orange-400 mb-6">
              It All Started with a Connection...
            </h3>

            <div className="space-y-6 text-lg text-gray-300 leading-relaxed">
              <p>
                Remember the distinct sound of a dial-up modem connecting, or the thrill of discovering 
                communities on platforms like <strong>Orkut</strong>? We do. Hash Adz was born from that era 
                of genuine digital discovery.
              </p>
              
              <p>
                We lived through the excitement of the internet's infancy, understanding how static pages 
                transformed into dynamic experiences. This deep-rooted, firsthand experience of the web's 
                evolution fuels our unique perspective today, allowing us to connect with brands and their 
                audiences on a foundational level.
              </p>
            </div>

            {/* Timeline Elements */}
            <div className="mt-12 flex items-center gap-8">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                  <i className="ri-wifi-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-orange-400 font-semibold">Dial-up Era</div>
                  <div className="text-gray-400 text-sm">Where it all began</div>
                </div>
              </div>
              
              <div className="w-16 h-0.5 bg-gradient-to-r from-orange-500 to-red-500"></div>
              
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center">
                  <i className="ri-global-line text-white text-xl"></i>
                </div>
                <div>
                  <div className="text-orange-400 font-semibold">Web Evolution</div>
                  <div className="text-gray-400 text-sm">Growing with the internet</div>
                </div>
              </div>
            </div>
          </div>

          {/* Visual */}
          <div className={`transition-all duration-1000 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'
          }`}>
            <div className="relative">
              {/* Main Image */}
              <div className="relative overflow-hidden rounded-2xl shadow-2xl">
                <img
                  src="/assets/genesis1.jpg"
                  alt="Early Days of Digital"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
              </div>

              {/* Floating Elements */}
              <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center animate-bounce">
                <i className="ri-computer-line text-white text-2xl"></i>
              </div>
              
              <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-gradient-to-r from-red-500 to-pink-500 rounded-full flex items-center justify-center animate-pulse">
                <i className="ri-heart-line text-white text-xl"></i>
              </div>

              {/* Decorative Lines */}
              <div className="absolute top-1/2 -left-8 w-16 h-0.5 bg-gradient-to-r from-transparent to-orange-500"></div>
              <div className="absolute top-1/3 -right-8 w-12 h-0.5 bg-gradient-to-l from-transparent to-red-500"></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}